<template>
  <div class="extranet-layout">
    <!-- Sidebar Navigation -->
    <aside class="extranet-sidebar" :class="{ collapsed: sidebarCollapsed }">
      <!-- Logo Section -->
      <div class="sidebar-header">
        <div class="logo-section">
          <img 
            src="/img/logos/extranet-logo.avif" 
            alt="Extranet Logo" 
            class="logo-img"
            :class="{ 'collapsed': sidebarCollapsed }"
          >
        </div>
      </div>

      <!-- Navigation Menu -->
      <nav class="sidebar-nav">
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/extranet/dashboard" class="nav-link">
              <i class="fas fa-tachometer-alt"></i>
              <span v-if="!sidebarCollapsed">Dashboard</span>
            </router-link>
          </li>
          
          <li class="nav-item">
            <router-link to="/extranet/corporate" class="nav-link" :class="{ 'router-link-active': isActive('corporate') }">
              <i class="fas fa-building"></i>
              <span v-if="!sidebarCollapsed">Corporate</span>
            </router-link>
          </li>
          
          <li class="nav-item">
            <router-link to="/extranet/system-log" class="nav-link">
              <i class="fas fa-file-alt"></i>
              <span v-if="!sidebarCollapsed">System Log</span>
            </router-link>
          </li>
          
          <li class="nav-item">
            <router-link to="/extranet/api-log" class="nav-link">
              <i class="fas fa-code"></i>
              <span v-if="!sidebarCollapsed">API Log</span>
            </router-link>
          </li>

          <!-- Divider -->
          <li v-if="hasTenantSelected" class="nav-divider">
            <span v-if="!sidebarCollapsed">Corporate Management</span>
          </li>
          
          <li v-if="hasTenantSelected" class="nav-item">
            <router-link to="/extranet/branches" class="nav-link" :class="{ 'router-link-active': isActive('branches') }">
              <i class="fas fa-code-branch"></i>
              <span v-if="!sidebarCollapsed">Branches</span>
            </router-link>
          </li>
          
          <li v-if="hasTenantSelected" class="nav-item">
            <router-link :to="{ name: 'ExtranetMerchants' }" class="nav-link" :class="{ 'router-link-active': isActive('merchants') }">
              <i class="fas fa-store"></i>
              <span v-if="!sidebarCollapsed">Merchants</span>
            </router-link>
          </li>
          
          <li v-if="hasTenantSelected" class="nav-item">
            <router-link to="/extranet/feature-flags" class="nav-link" :class="{ 'router-link-active': isActive('feature-flags') }">
              <i class="fas fa-flag"></i>
              <span v-if="!sidebarCollapsed">Feature Flags</span>
            </router-link>
          </li>

          <!-- Disabled Tenant Menu (when no tenant selected) -->
          <li v-if="!hasTenantSelected" class="nav-divider">
            <span v-if="!sidebarCollapsed">Corporate Management</span>
          </li>
          
          <li v-if="!hasTenantSelected" class="nav-item">
            <div class="nav-link nav-link-disabled" title="Select a tenant first">
              <i class="fas fa-code-branch"></i>
              <span v-if="!sidebarCollapsed">Branches</span>
              <i v-if="!sidebarCollapsed" class="fas fa-lock lock-icon"></i>
            </div>
          </li>
          
          <li v-if="!hasTenantSelected" class="nav-item">
            <div class="nav-link nav-link-disabled" title="Select a tenant first">
              <i class="fas fa-store"></i>
              <span v-if="!sidebarCollapsed">Merchants</span>
              <i v-if="!sidebarCollapsed" class="fas fa-lock lock-icon"></i>
            </div>
          </li>
          
          <li v-if="!hasTenantSelected" class="nav-item">
            <div class="nav-link nav-link-disabled" title="Select a tenant first">
              <i class="fas fa-flag"></i>
              <span v-if="!sidebarCollapsed">Feature Flags</span>
              <i v-if="!sidebarCollapsed" class="fas fa-lock lock-icon"></i>
            </div>
          </li>
        </ul>
      </nav>

      <!-- Sidebar Footer -->
      <div class="sidebar-footer">
        <!-- User Profile Section -->
        <div class="user-profile-section" :class="{ 'collapsed': sidebarCollapsed }">
          <div class="avatar-wrapper">
            <img v-if="userAvatar" :src="userAvatar" class="avatar-img" alt="User">
            <div v-else class="avatar-initials">{{ userInitials }}</div>
          </div>
          <div v-if="!sidebarCollapsed" class="user-info-text">
            <span class="user-name-text">{{ userName }}</span>
            <span class="user-role-text">Root Admin</span>
          </div>
        </div>
        
        <!-- Action Buttons Section -->
        <div class="sidebar-action-section" :class="{ 'collapsed': sidebarCollapsed }">
          <button @click="handleLogout" class="minimal-action-btn logout-btn" title="Logout">
            <div class="action-icon-circle logout-icon">
              <i class="fas fa-power-off"></i>
            </div>
            <span v-if="!sidebarCollapsed">Logout</span>
          </button>
          
          <button @click="toggleSidebar" class="minimal-action-btn">
            <div class="action-icon-circle">
              <i class="fas" :class="sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
            </div>
            <span v-if="!sidebarCollapsed">Collapse Sidebar</span>
          </button>
        </div>
      </div>
    </aside>

    <!-- Main Content Area -->
    <main class="extranet-main" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
      <!-- Header -->
      <header class="extranet-header">
        <div class="header-left">
          <div class="extranet-breadcrumb">
            <span class="breadcrumb-item breadcrumb-parent">{{ breadcrumbs[0] }}</span>
            <i class="fas fa-chevron-right breadcrumb-separator"></i>
            <span class="breadcrumb-item breadcrumb-current">{{ breadcrumbs[1] }}</span>
          </div>
        </div>
        <div class="header-right">
          <!-- No Tenant Selected Banner -->
          <div v-if="!hasTenantSelected" class="tenant-alert">
            <div class="alert-icon">
              <i class="fas fa-info-circle"></i>
            </div>
            <div class="alert-content">
              <span class="alert-text">Please select a tenant to access management features</span>
            </div>
            <i class="fas fa-arrow-right alert-arrow"></i>
          </div>
          
          <!-- Open Tenant Button -->
          <a v-if="hasTenantSelected && selectedTenantUrl"
            :href="selectedTenantUrl"
            target="_blank"
            class="btn-open-tenant"
            title="Open selected tenant in new tab">
            <i class="fas fa-external-link-alt"></i>
            <span>Open Tenant</span>
          </a>
          
          <!-- Tenant Selector -->
          <tenant-selector />
        </div>
      </header>

      <!-- Content -->
      <div class="extranet-content">
        <router-view :key="$route.fullPath" />
      </div>
    </main>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import TenantSelector from '@/components/extranet/TenantSelector.vue'

export default {
  name: 'ExtranetLayout',
  
  components: {
    TenantSelector
  },
  
  data() {
    return {
      sidebarCollapsed: false
    }
  },
  
  computed: {
    ...mapState({
      selectedTenant: state => state.extranet?.selectedTenant
    }),
    ...mapGetters({
      hasTenantSelected: 'extranet/hasTenantSelected',
      currentTenantName: 'extranet/currentTenantName'
    }),
    selectedTenantUrl() {
      if (this.selectedTenant?.domain) {
        return `http://${this.selectedTenant.domain}`
      }
      return null
    },
    userName() {
      const user = this.$store.state.Auth?.currUser
      return user?.name || user?.email || 'Admin'
    },
    userInitials() {
      const name = this.userName;
      if (!name) return 'A';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },
    userAvatar() {
      return this.$store.state.Auth?.currUser?.profile_photo_url || null;
    },
    breadcrumbs() {
      const breadcrumbMap = {
        'ExtranetDashboard': ['Dashboard', 'Extranet Overview'],
        'ExtranetCorporate': ['Management', 'Tenants List'],
        'ExtranetCorporateForm': ['Management', 'Create Tenant'],
        'ExtranetCorporateEdit': ['Management', 'Edit Tenant'],
        'ExtranetSystemLog': ['Logs', 'System Activity'],
        'ExtranetApiLog': ['Logs', 'API Traffic'],
        'ExtranetBranches': ['Tenant Admin', 'Branches'],
        'ExtranetMerchants': ['Tenant Admin', 'Merchants'],
        'ExtranetFeatureFlags': ['Settings', 'Feature Flags']
      };
      
      return breadcrumbMap[this.$route.name] || ['Extranet', 'Management'];
    }
  },
  
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    handleLogout() {
      // Clear extranet state
      this.$store.dispatch('extranet/clearTenant')
      
      // Logout user
      this.$store.dispatch('Auth/logout')
        .then(() => {
          this.$router.push('/extranet/login')
        })
        .catch(error => {
          console.error('Logout error:', error)
          this.$router.push('/extranet/login')
        })
    },
    isActive(prefix) {
      const path = this.$route.path;
      if (prefix === 'corporate') {
        return path.startsWith('/extranet/corporate');
      }
      if (prefix === 'branches') {
        return path.startsWith('/extranet/branches') || path.startsWith('/extranet/tenant-admin/branches');
      }
      if (prefix === 'merchants') {
        return path.startsWith('/extranet/merchants') || path.startsWith('/extranet/tenant-admin/merchants');
      }
      if (prefix === 'feature-flags') {
        return path.startsWith('/extranet/feature-flags');
      }
      return false;
    }
  }
}
</script>

<style scoped>
/* Layout Container */
.extranet-layout {
  --extranet-primary: #b51a1f;
  --extranet-secondary: #3d3d3d;
  --extranet-white: #ffffff;
  --extranet-accent: #e1e5ea;
  --extranet-rounded: 12px;
  --header-height: 70px;
  
  display: flex;
  min-height: 100vh;
  background-color: #f5f6fa;
}

/* Sidebar */
.extranet-sidebar {
  width: 232px;
  background-color: var(--extranet-secondary);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.05);
}

.extranet-sidebar.collapsed {
  width: 70px;
}

/* Sidebar Header */
.sidebar-header {
  padding: 12px;
  background-color: var(--extranet-white);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo-section {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.logo-img {
  height: 45px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
}

.logo-img.collapsed {
  height: 25px;
}

.btn-open-tenant {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  color: var(--extranet-secondary);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  height: 48px;
  box-sizing: border-box;
}

.btn-open-tenant:hover {
  background: white;
  border-color: var(--extranet-primary);
  color: var(--extranet-primary);
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
}

.btn-open-tenant i {
  font-size: 12px;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  margin-bottom: 4px;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
  font-size: 14px;
  position: relative;
}

.nav-link:hover {
  color: #ffffff;
}

.nav-link.router-link-active {
  color: #ffffff;
}

.nav-link.router-link-active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background-color: #ffffff;
  border-radius: 0 4px 4px 0;
}

.nav-link i {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

/* Disabled Nav Link */
.nav-link-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  position: relative;
}

.nav-link-disabled:hover {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.6);
}

.lock-icon {
  margin-left: auto;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

/* Nav Divider */
.nav-divider {
  padding: 16px 20px 8px;
  margin-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-divider span {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* Sidebar Footer Restyle */
.sidebar-footer {
  padding: 12px;
  background-color: var(--extranet-primary);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-profile-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.user-profile-section.collapsed {
  padding: 8px;
  justify-content: center;
  background: transparent;
}

.avatar-wrapper {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  font-size: 14px;
  font-weight: 700;
  color: var(--extranet-primary);
}

.user-info-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.user-name-text {
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role-text {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sidebar-action-section {
  padding-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sidebar-action-section.collapsed {
  align-items: center;
}

.minimal-action-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px;
}

.minimal-action-btn:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
}

.minimal-action-btn.logout-btn:hover {
  color: #ffb3b3;
  background: rgba(220, 38, 38, 0.15);
}

.action-icon-circle {
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.logout-icon {
  background: rgba(220, 38, 38, 0.2);
  color: #fca5a5;
}

.minimal-action-btn:hover .action-icon-circle {
  background: rgba(255, 255, 255, 0.2);
}

.minimal-action-btn.logout-btn:hover .logout-icon {
  background: rgba(220, 38, 38, 0.4);
  color: #fee2e2;
}

.action-icon-circle i {
  font-size: 11px;
}

/* Main Content */
.extranet-main {
  flex: 1;
  width: 100%;
  padding-left: 232px;
  transition: padding-left 0.3s ease;
  display: flex;
  flex-direction: column;
}

.extranet-main.sidebar-collapsed {
  padding-left: 70px;
}

/* Header */
.extranet-header {
  background-color: var(--extranet-white);
  height: var(--header-height);
  padding: 0 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
}

.header-left {
  display: flex;
  align-items: center;
}

.extranet-breadcrumb {
  display: flex;
  align-items: center;
  gap: 12px;
}

.breadcrumb-item {
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-parent {
  color: var(--extranet-secondary);
}

.breadcrumb-separator {
  font-size: 10px;
  color: #cbd5e1;
}

.breadcrumb-current {
  color: var(--extranet-secondary);
  font-weight: 600;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* Tenant Alert Restyle */
.tenant-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  background: #fff8f8;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  height: 52px; /* Matching selector trigger height */
  box-sizing: border-box;
}

.alert-icon {
  color: var(--extranet-primary);
  font-size: 14px;
  flex-shrink: 0;
  opacity: 0.8;
}

.alert-content {
  flex: 1;
}

.alert-text {
  font-size: 12px;
  color: var(--extranet-primary);
  font-weight: 500;
  line-height: 1.4;
}

.alert-arrow {
  font-size: 12px;
  color: var(--extranet-primary);
  flex-shrink: 0;
  opacity: 0.5;
}

/* Content */
.extranet-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .extranet-sidebar {
    width: 70px;
  }
  
  .extranet-sidebar .logo-text,
  .extranet-sidebar .nav-link span,
  .extranet-sidebar .nav-divider span,
  .extranet-sidebar .user-details,
  .extranet-sidebar .logout-btn-sidebar span,
  .extranet-sidebar .collapse-btn span,
  .extranet-sidebar .lock-icon {
    display: none;
  }
  
  .user-info-sidebar {
    justify-content: center;
    padding: 8px;
  }
  
  .extranet-main {
    padding-left: 70px;
  }
  
  .extranet-header {
    padding: 12px 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .header-title {
    font-size: 16px;
  }

  .header-right {
    flex-direction: column;
    gap: 12px;
  }

  .tenant-alert {
    width: 100%;
  }

  .alert-text {
    font-size: 12px;
  }
  
  .extranet-content {
    padding: 16px;
  }
}

/* Scrollbar Styling */
.extranet-sidebar::-webkit-scrollbar {
  width: 6px;
}

.extranet-sidebar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.extranet-sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.extranet-sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
